import { Button } from "@chakra-ui/button"
import { useColorModeValue } from "@chakra-ui/color-mode"
import { Box, Flex, Heading, Stack } from "@chakra-ui/layout"
import Link from "next/link"
import { useRouter } from "next/router"
import AppContainer from "src/common/AppContainer"
import FadeInOnMount from "src/common/FadeInOnMount"
import HomepageSnippets from "src/common/HomepageSnippets"
import Topic from "src/common/Topic"
import UnclaimedOrgPitch from "src/domain/UnclaimedOrgPitch"
import { useAccent2, useBackground } from "src/modeAwareColors"
import { routes } from "src/navigation/util"
import FilterableAutocomplete, { renderMainIndexHit } from "src/search/FilterableAutocomplete"
import SearchableFeedProvider from "src/search/SearchableFeedProvider"
import { useLightModeValue } from "src/util/theme"
import { A, H1, H2 } from "src/typography/typography"
import analytics from "src/util/analytics"
import useIsSignedIn from "src/hooks/useIsSignedIn"
import { AlgoliaIndexes } from "projectlink-common"
import useSWR from "swr"
import endpoints from "src/access/endpoints"
import { Skeleton } from "@chakra-ui/skeleton"

function TopicsRow({ topics }) {
  if (!topics) {
    return (
      <Flex>
        <Skeleton w='25%' h='80px' mr='base' />
        <Skeleton w='25%' h='80px' mr='base' />
        <Skeleton w='25%' h='80px' mr='base' />
        <Skeleton w='25%' h='80px' />
      </Flex>
    )
  }

  const t = topics.slice(0, 4)

  return (
    <Flex flexWrap='wrap'>
      {
        t.map(topic => (
          <Box
            w={['100%', '100%', '25%', '25%']}
            pr={[0, 0, 'base', 'base']}
            mb={['md', 'md', 0, 0]}
            key={topic.name}
            _last={{
              pr: [0, 0, 0, 0],
            }}
          >
            <Topic topic={topic} />
          </Box>
        ))
      }
    </Flex>
  )
}

function Explore({ orgs, initialTopics, projects }) {
  const isSignedIn = useIsSignedIn()
  const router = useRouter()
  const { data: topics } = useSWR(endpoints.topics, {
    initialData: initialTopics,
  })

  const onClickHit = async (hit) => {
    analytics.logEvent('click_search_hit')
    await router.push(routes[hit.type](hit.objectID))
  }

  const boxShadow = useLightModeValue('lg')
  const bgImage = useColorModeValue(
    "url('https://storage.googleapis.com/projectlink-dev.appspot.com/projectlink-stacks.jpg') center center",
    "url('https://storage.googleapis.com/projectlink-dev.appspot.com/projectlink-hero-dark.jpeg') center center")

  return (
    <>
      <Flex
        display={['none', 'none', 'flex']}
        bg={bgImage}
        bgSize={['cover', 'cover', 'cover']}
        h='400px'
        w='100%'
        maxW='1440px'
        mx='auto'
        mb='-94px'
      >
        <Box
          maxW='container.lg'
          w='100%'
          mx='auto'
          mt='90px'
          px={5}
        >
          <FadeInOnMount>
            <Heading
              color='white'
              fontFamily='brand'
              fontWeight='normal'
              bottom='sm'
              right='md'
            >
              Projectlink
            </Heading>
            <H1
              color='white'
            >
              Find industrial projects, <br /> manufacturers and suppliers
          </H1>
          </FadeInOnMount>
        </Box>
      </Flex>
      <AppContainer pb={28}>
        <SearchableFeedProvider indexName={AlgoliaIndexes.main}>
          <FadeInOnMount>
            <Box
              borderRadius='xl'
              borderColor={useAccent2()}
              borderWidth='1px'
              bg={useBackground()}
              boxShadow={boxShadow}
              mx='auto'
              p='base'
              pb={4}
            >
              <FilterableAutocomplete
                renderHit={renderMainIndexHit}
                placeholder='Search companies, projects, or topics...'
                onClickHit={onClickHit}
                topics={topics}
                inputGroupProps={{
                  mb: 4,
                }}
              />
            </Box>
          </FadeInOnMount>
        </SearchableFeedProvider>
        <Stack mt='lg' spacing='md'>
          <Flex justify='space-between' align='baseline'>
            <H2>
              Explore topics
            </H2>
            <Link href={routes.topics} passHref>
              <A fontSize='sm'>
                All topics →
              </A>
            </Link>
          </Flex>
          <TopicsRow topics={topics} />
        </Stack>
        <Stack mt='lg' spacing='md'>
          <Flex justify='space-between' align='baseline'>
            <H2>
              Companies
            </H2>
            <Link href={routes.companies} passHref>
              <A fontSize='sm'>
                All companies →
              </A>
            </Link>
          </Flex>
          <HomepageSnippets
            snippets={orgs}
          />
        </Stack>
        <Stack mt='lg' spacing='md'>
          <Flex justify='space-between' align='baseline'>
            <H2>
              Projects
            </H2>
            <Link href={routes.projects} passHref>
              <A fontSize='sm'>
                All projects →
                </A>
            </Link>
          </Flex>
          <HomepageSnippets
            snippets={projects}
          />
        </Stack>
        {!isSignedIn && (
          <Stack
            align='center'
            spacing={20}
            mt={20}
            textAlign='center'
          >
            <H2>
              Projectlink connects companies
            </H2>
            <UnclaimedOrgPitch mt='base' />
            <Link href={routes.signup} passHref>
              <Button
                size='md'
                variant='success'
              >
                Sign up
              </Button>
            </Link>
          </Stack>
        )}
      </AppContainer>
    </>
  )
}

export default Explore
